/**
 * AppStoreStudio — In-Browser Screenshot-Generator für App Store & Google Play
 *
 * Was er kann:
 * - Upload eigener App-Screenshots (PNG/JPG)
 * - Auswahl aus 5 Marketing-Templates (Farbverlauf, iPhone-Frame, Headline)
 * - Editierbare Headline + Subtitle pro Screenshot
 * - Export als PNG in App-Store-konformen Auflösungen:
 *   - 6.7" iPhone (1290×2796) → Pflicht für App Store
 *   - 6.5" iPhone (1242×2688) → Fallback
 *   - 2.5K Play Store (1440×2560) → Play Store
 * - Bulk-Export: alle Templates gleichzeitig als ZIP nicht nötig,
 *   wir rendern einzeln + Download pro Klick.
 *
 * Nutzung: `/admin/mobile-app` → Tab "App Store Studio"
 */
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import {
  Smartphone, Upload, Download, Layout, Palette,
  RefreshCw, Wand2, Image as ImageIcon,
} from 'lucide-react';

// =========================================================================
// TEMPLATES — vorgefertigte Designs mit Farbverlauf, Text-Position, Frame
// =========================================================================

const TEMPLATES = [
  {
    id: 'bold-gold',
    name: 'Bold Gold',
    background: 'linear-gradient(135deg, #1A1A1A 0%, #0B0B0F 100%)',
    accent: '#EAB308',
    headlineColor: '#EAB308',
    subtitleColor: '#FFFFFF',
    textPosition: 'top',
    showFrame: true,
  },
  {
    id: 'clean-white',
    name: 'Clean White',
    background: 'linear-gradient(180deg, #F9FAFB 0%, #E5E7EB 100%)',
    accent: '#0F172A',
    headlineColor: '#0F172A',
    subtitleColor: '#475569',
    textPosition: 'top',
    showFrame: true,
  },
  {
    id: 'gradient-sunset',
    name: 'Sunset',
    background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 50%, #DB2777 100%)',
    accent: '#FFFFFF',
    headlineColor: '#FFFFFF',
    subtitleColor: '#FEF3C7',
    textPosition: 'top',
    showFrame: true,
  },
  {
    id: 'dark-sleek',
    name: 'Dark Sleek',
    background: 'linear-gradient(160deg, #020617 0%, #1E293B 100%)',
    accent: '#22D3EE',
    headlineColor: '#FFFFFF',
    subtitleColor: '#94A3B8',
    textPosition: 'bottom',
    showFrame: true,
  },
  {
    id: 'energy-purple',
    name: 'Energy',
    background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
    accent: '#FDE68A',
    headlineColor: '#FFFFFF',
    subtitleColor: '#E0E7FF',
    textPosition: 'top',
    showFrame: true,
  },
];

// =========================================================================
// SIZES — App Store + Play Store Pflichtauflösungen
// =========================================================================

const SIZES = [
  { id: '6_7', label: 'iPhone 6.7"', width: 1290, height: 2796, store: 'App Store' },
  { id: '6_5', label: 'iPhone 6.5"', width: 1242, height: 2688, store: 'App Store (Fallback)' },
  { id: 'play', label: 'Play Store Phone', width: 1440, height: 2560, store: 'Google Play' },
];

// =========================================================================
// DEFAULT SLIDES — 5 Marketing-Folien mit Vorschlagstexten
// =========================================================================

const DEFAULT_SLIDES = [
  {
    headline: 'Dein persönlicher\nFitness-Coach',
    subtitle: 'Trainings, Ernährung und Fortschritt — alles in einer App.',
    templateId: 'bold-gold',
    screenshot: null,
  },
  {
    headline: 'Makros auf\neinen Blick',
    subtitle: 'Kalorien, Protein, Carbs & Fett — live und interaktiv.',
    templateId: 'clean-white',
    screenshot: null,
  },
  {
    headline: 'Termine buchen\nin Sekunden',
    subtitle: 'Drag & drop, live Verfügbarkeit, sofort bestätigt.',
    templateId: 'gradient-sunset',
    screenshot: null,
  },
  {
    headline: 'KI-Coach\nfür jeden Tag',
    subtitle: 'Analysiert deinen Fortschritt und gibt dir smarte Tipps.',
    templateId: 'dark-sleek',
    screenshot: null,
  },
  {
    headline: 'Punkte\nsammeln & teilen',
    subtitle: 'Familienpakete, Bonus-Aktionen und flexible Abos.',
    templateId: 'energy-purple',
    screenshot: null,
  },
];

// =========================================================================
// MAIN COMPONENT
// =========================================================================

const AppStoreStudio = () => {
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [activeIdx, setActiveIdx] = useState(0);
  const [size, setSize] = useState(SIZES[0]);
  const [exporting, setExporting] = useState(false);

  const slide = slides[activeIdx];
  const template = TEMPLATES.find((t) => t.id === slide.templateId) || TEMPLATES[0];

  const previewRef = useRef(null);
  const exportRef = useRef(null);

  // ---- Handlers ----

  const updateSlide = (patch) =>
    setSlides((all) => all.map((s, i) => (i === activeIdx ? { ...s, ...patch } : s)));

  const handleScreenshotUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => updateSlide({ screenshot: e.target.result });
    reader.readAsDataURL(file);
  };

  const exportCurrent = async () => {
    if (!exportRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(exportRef.current, {
        width: size.width,
        height: size.height,
        pixelRatio: 1,
        cacheBust: true,
      });
      const link = document.createElement('a');
      link.download = `strongfit_${size.id}_slide-${activeIdx + 1}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(`Screenshot ${activeIdx + 1} exportiert (${size.width}×${size.height})`);
    } catch (e) {
      console.error(e);
      toast.error('Export fehlgeschlagen — hast du ein Screenshot hochgeladen?');
    } finally {
      setExporting(false);
    }
  };

  const exportAll = async () => {
    setExporting(true);
    try {
      for (let i = 0; i < slides.length; i++) {
        setActiveIdx(i);
        await new Promise((r) => setTimeout(r, 300)); // UI refresh
        if (!exportRef.current) continue;
        const dataUrl = await toPng(exportRef.current, {
          width: size.width,
          height: size.height,
          pixelRatio: 1,
          cacheBust: true,
        });
        const link = document.createElement('a');
        link.download = `strongfit_${size.id}_slide-${i + 1}.png`;
        link.href = dataUrl;
        link.click();
        await new Promise((r) => setTimeout(r, 500));
      }
      toast.success(`Alle ${slides.length} Screenshots exportiert`);
    } catch (e) {
      console.error(e);
      toast.error('Bulk-Export abgebrochen');
    } finally {
      setExporting(false);
    }
  };

  const resetDefaults = () => {
    setSlides(DEFAULT_SLIDES);
    setActiveIdx(0);
    toast.info('Auf Standardtexte zurückgesetzt');
  };

  // ---- Render ----

  return (
    <div className="space-y-6" data-testid="app-store-studio">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center">
          <Wand2 className="text-yellow-600" size={22} />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900">App Store Studio</h2>
          <p className="text-sm text-gray-500 mt-1">
            Erzeuge 5 Marketing-Screenshots für App Store &amp; Play Store in wenigen Minuten.
            Lade deine Roh-Screenshots aus TestFlight/Xcode/Android-Emulator hoch, wähle ein
            Template, passe den Text an → Export als PNG in der richtigen Auflösung.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={resetDefaults}
            className="text-xs text-gray-500 hover:text-gray-900 px-3 py-2 border border-gray-200 rounded-lg flex items-center gap-1"
          >
            <RefreshCw size={12} /> Zurücksetzen
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* ========== LEFT PANEL — Slides + Editor ========== */}
        <div className="space-y-4">
          {/* Slide picker */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              5 Marketing-Folien
            </h3>
            <div className="grid grid-cols-5 gap-2">
              {slides.map((s, i) => {
                const t = TEMPLATES.find((x) => x.id === s.templateId) || TEMPLATES[0];
                return (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    data-testid={`app-store-studio-slide-${i}`}
                    className={`aspect-[9/16] rounded-lg border-2 text-xs font-bold text-white transition-all ${
                      activeIdx === i ? 'border-yellow-500 scale-105' : 'border-gray-200 hover:border-gray-400'
                    }`}
                    style={{ background: t.background }}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Editor */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Folie {activeIdx + 1} anpassen
            </h3>

            <label className="block">
              <span className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                <ImageIcon size={12} /> App-Screenshot (PNG/JPG)
              </span>
              <label
                className="w-full bg-gray-100 border border-dashed border-gray-300 rounded-lg p-3 flex items-center justify-center gap-2 text-xs text-gray-500 cursor-pointer hover:border-yellow-500"
                data-testid="app-store-studio-upload"
              >
                <Upload size={14} />
                {slide.screenshot ? 'Anderes Bild wählen' : 'Screenshot hochladen'}
                <input type="file" accept="image/*" className="hidden" onChange={handleScreenshotUpload} />
              </label>
            </label>

            <label className="block">
              <span className="text-xs text-gray-500 mb-1 block">Headline (Enter = neue Zeile)</span>
              <textarea
                rows={2}
                value={slide.headline}
                onChange={(e) => updateSlide({ headline: e.target.value })}
                data-testid="app-store-studio-headline"
                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 resize-none font-bold"
              />
            </label>

            <label className="block">
              <span className="text-xs text-gray-500 mb-1 block">Subtitle</span>
              <textarea
                rows={2}
                value={slide.subtitle}
                onChange={(e) => updateSlide({ subtitle: e.target.value })}
                data-testid="app-store-studio-subtitle"
                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 resize-none"
              />
            </label>

            <label className="block">
              <span className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                <Palette size={12} /> Template
              </span>
              <div className="grid grid-cols-5 gap-1">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => updateSlide({ templateId: t.id })}
                    title={t.name}
                    className={`aspect-square rounded-md border-2 transition-all ${
                      slide.templateId === t.id ? 'border-yellow-500 scale-110' : 'border-gray-200'
                    }`}
                    style={{ background: t.background }}
                  />
                ))}
              </div>
              <div className="text-[10px] text-gray-500 mt-1">Aktiv: {template.name}</div>
            </label>
          </div>

          {/* Size + Export */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
              <Layout size={12} /> Export-Grösse
            </h3>
            <select
              value={size.id}
              onChange={(e) => setSize(SIZES.find((s) => s.id === e.target.value))}
              data-testid="app-store-studio-size"
              className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
            >
              {SIZES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label} — {s.width}×{s.height} · {s.store}
                </option>
              ))}
            </select>

            <button
              onClick={exportCurrent}
              disabled={exporting}
              data-testid="app-store-studio-export"
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download size={14} /> Diese Folie exportieren
            </button>
            <button
              onClick={exportAll}
              disabled={exporting}
              data-testid="app-store-studio-export-all"
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
            >
              <Download size={12} /> Alle 5 exportieren
            </button>
          </div>
        </div>

        {/* ========== RIGHT PANEL — Preview ========== */}
        <div>
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Vorschau (verkleinert)
              </h3>
              <span className="text-[10px] text-gray-500">
                Export: {size.width}×{size.height} px
              </span>
            </div>

            {/* Scaled preview — shown to user */}
            <div className="flex justify-center">
              <div
                ref={previewRef}
                style={{
                  width: `${size.width * 0.18}px`,
                  height: `${size.height * 0.18}px`,
                  background: template.background,
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 12,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                }}
              >
                <SlideContent slide={slide} template={template} scale={0.18} />
              </div>
            </div>
          </div>

          {/* Hidden full-size export canvas */}
          <div style={{ position: 'fixed', top: -99999, left: -99999, pointerEvents: 'none' }}>
            <div
              ref={exportRef}
              style={{
                width: `${size.width}px`,
                height: `${size.height}px`,
                background: template.background,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <SlideContent slide={slide} template={template} scale={1} />
            </div>
          </div>
        </div>
      </div>

      {/* Tip box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-sm text-blue-200 flex gap-3">
        <Smartphone size={18} className="flex-shrink-0 mt-0.5" />
        <div>
          <strong>Tipp:</strong> Nimm die Roh-Screenshots direkt im iPhone/Android-Emulator auf oder auf
          einem echten Gerät (iPhone: Lauter + Power-Button gleichzeitig). Ideal ist die Grösse
          1290×2796 (iPhone 15 Pro Max) — Apple akzeptiert als Haupt-Pflichtgrösse. Play Store fordert
          mindestens 1440×2560.
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// SLIDE CONTENT — wird 2× gerendert: Preview + Export
// =========================================================================

const SlideContent = ({ slide, template, scale }) => {
  const px = (v) => `${v * scale}px`;

  return (
    <>
      {/* Noise/Grain overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 200 200\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/><feColorMatrix values=\'0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\'/></svg>")',
          opacity: 0.4,
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
        }}
      />

      {/* Text area */}
      <div
        style={{
          position: 'absolute',
          top: px(150),
          left: px(80),
          right: px(80),
          textAlign: 'center',
          zIndex: 2,
        }}
      >
        <h1
          style={{
            color: template.headlineColor,
            fontSize: px(112),
            fontWeight: 900,
            lineHeight: 1.05,
            letterSpacing: px(-2),
            margin: 0,
            fontFamily: "'SF Pro Display', 'Inter', system-ui, sans-serif",
            textShadow: template.id === 'bold-gold' ? `0 0 ${px(30)} rgba(234,179,8,0.3)` : 'none',
            whiteSpace: 'pre-line',
          }}
        >
          {slide.headline}
        </h1>
        <p
          style={{
            color: template.subtitleColor,
            fontSize: px(44),
            fontWeight: 500,
            marginTop: px(30),
            lineHeight: 1.3,
            fontFamily: "'SF Pro Text', 'Inter', system-ui, sans-serif",
            opacity: 0.9,
          }}
        >
          {slide.subtitle}
        </p>
      </div>

      {/* iPhone device frame with screenshot */}
      <div
        style={{
          position: 'absolute',
          bottom: px(-80),
          left: '50%',
          transform: 'translateX(-50%)',
          width: px(900),
          height: px(1840),
          background: '#111',
          borderRadius: px(120),
          boxShadow: `0 ${px(30)} ${px(80)} rgba(0,0,0,0.5), 0 0 0 ${px(12)} #222`,
          overflow: 'hidden',
          border: `${px(16)} solid #1a1a1a`,
        }}
      >
        {/* Dynamic Island */}
        <div
          style={{
            position: 'absolute',
            top: px(35),
            left: '50%',
            transform: 'translateX(-50%)',
            width: px(260),
            height: px(70),
            background: '#000',
            borderRadius: px(40),
            zIndex: 10,
          }}
        />

        {/* Screenshot */}
        {slide.screenshot ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={slide.screenshot}
            alt="App Screenshot"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #1f1f1f 0%, #0f0f0f 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
              fontSize: px(40),
              fontWeight: 600,
              textAlign: 'center',
              padding: px(40),
            }}
          >
            {scale > 0.5 ? 'Lade App-Screenshot hoch →' : '📱'}
          </div>
        )}
      </div>
    </>
  );
};

export default AppStoreStudio;
